import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const ShareButton = ({
  charity,
  className = '',
  children = 'Chia sẻ',
  onPopupOpen,
  onPopupClose,
}) => {
  const [showSharePopup, setShowSharePopup] = useState(false);

  const closeSharePopup = () => {
    setShowSharePopup(false);
    if (onPopupClose) onPopupClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSharePopup();
    }
  };

  const handleShareClick = () => {
    setShowSharePopup(true);
    if (onPopupOpen) onPopupOpen();
  };

  const copyToClipboard = async () => {
    if (!charity) return;

    const shareUrl = window.location.href;
    const shareText = `🌟 GK88 - ${charity.title}

${charity.description}

📊 Thống kê:
• Quỹ chia sẻ: ${new Intl.NumberFormat('vi-VN').format(charity.countShare * 50000 || 0)} VND
• Lượt chia sẻ: ${new Intl.NumberFormat('vi-VN').format(charity.countShare)} lượt

🔗 Xem chi tiết: ${shareUrl}

#GK88 #TừThiện #ChiaSẻYêuThương #ThiệnNguyện`;

    try {
      await navigator.clipboard.writeText(shareText);
      // Hiển thị thông báo thành công
      alert('Đã copy bài viết vào clipboard! Bạn có thể paste vào bất kỳ đâu.');
    } catch (err) {
      // Fallback cho trình duyệt cũ
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Đã copy bài viết vào clipboard! Bạn có thể paste vào bất kỳ đâu.');
    }
  };

  const shareToSocial = (platform) => {
    if (!charity) return;

    const shareUrl = window.location.href;
    const shareText = `GK88 - ${charity.title}\n\n${charity.description}`;

    let shareLink = '';

    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          shareUrl,
        )}&text=${encodeURIComponent(shareText)}`;
        break;
      case 'telegram':
        shareLink = `https://t.me/share/url?url=${encodeURIComponent(
          shareUrl,
        )}&text=${encodeURIComponent(shareText)}`;
        break;
      default:
        return;
    }

    window.open(shareLink, '_blank', 'width=600,height=400');
    closeSharePopup();
  };

  return (
    <>
      <button
        onClick={handleShareClick}
        className={`bg-[#fe6601] hover:bg-[#e55a00] text-white font-semibold transition-colors duration-200 ${className}`}
      >
        {children}
      </button>

      {/* Share Popup - Rendered using Portal to document.body */}
      {showSharePopup &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] animate-fadeIn"
            onClick={handleBackdropClick}
          >
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl animate-slideUp">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Chia sẻ</h3>
                <button
                  onClick={closeSharePopup}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Social Media Buttons */}
              <div className="mb-6">
                <div className="grid grid-cols-3 gap-4">
                  {/* Facebook */}
                  <button
                    onClick={() => shareToSocial('facebook')}
                    className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700">Facebook</span>
                  </button>

                  {/* X (Twitter) */}
                  <button
                    onClick={() => shareToSocial('twitter')}
                    className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700">X</span>
                  </button>

                  {/* Telegram */}
                  <button
                    onClick={() => shareToSocial('telegram')}
                    className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700">Telegram</span>
                  </button>
                </div>
              </div>

              {/* Copy Button - Full width */}
              <div className="border-t pt-4">
                <button
                  onClick={copyToClipboard}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy bài viết
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Copy toàn bộ thông tin để chia sẻ ở bất kỳ đâu
                </p>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ShareButton;
